import { inject, Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { delay, filter, switchMap, take, tap } from 'rxjs/operators';
import { CROSS_SELLING_STORE_API_ACTIONS } from '@store/cross-selling/cross-selling.action';
import { statedStream } from '@shared/utils/rxjs/stated-steam';
import { statedDataToAction } from '@shared/utils/rxjs/stated-data-to-action';
import { DoodApiError } from '@shared/interfaces/error.interface';
import { CrossSellingApiService } from '@core/services/api/cross-selling/cross-selling-api.service';
import { combineLatest, EMPTY, of } from 'rxjs';
import { DoodCartModel } from '@core/models/cart.model';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { OrdersService } from '@core/services/orders/orders.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { CrossSellingModalModalConfig } from '@shared/modals/cross-selling-modal/cross-selling-modal.component';
import { CrossSellingProposals } from '@store/cross-selling/cross-selling.model';
import { PRODUCT_STORE_COMPONENT_ACTIONS } from '@store/product/product.action';
import { CART_ACTIONS } from '@store/cart';
import { Store } from '@ngrx/store';
import { selectCrossSellingData } from '@store/cross-selling/cross-selling.selector';

@Injectable({
  providedIn: 'root',
})
export class CrossSellingEffects {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly crossSellingApiService = inject(CrossSellingApiService);
  private readonly shopStoreSelector = inject(ShopStoreSelector);
  private readonly cartStoreSelector = inject(CartStoreSelector);
  private readonly ordersService = inject(OrdersService);
  private readonly modalsService: ModalsService<CrossSellingModalModalConfig> = inject(
    ModalsService<CrossSellingModalModalConfig>,
  );

  // There may be a case where the cart is not created or not associated with the product's shop. For example, if the marketplace has multiple shops and we create a cart in one but access another shop.
  _loadCrossSellingEffect = createEffect(() => {
    return this.actions$.pipe(ofType(PRODUCT_STORE_COMPONENT_ACTIONS.selectProduct)).pipe(
      switchMap(({ productId, productShopId }) => {
        const cart = this.cartStoreSelector.selectActive();
        const shop = this.shopStoreSelector.selectActive();

        if (!shop) {
          return EMPTY;
        }

        const order = this.ordersService.mapCartToOrderValues(cart as DoodCartModel, []);

        return statedDataToAction(
          statedStream(
            this.crossSellingApiService.getCrossSellingSuggestions$({
              trigger_product_id: productId,
              trigger_product_shop_id: productShopId || shop.id,
              order,
            }),
          ),
          {
            loading: () => CROSS_SELLING_STORE_API_ACTIONS.loadCrossSellingLoading(),
            failure: (error: DoodApiError) =>
              CROSS_SELLING_STORE_API_ACTIONS.loadCrossSellingFailure({ error }),
            success: data => {
              return CROSS_SELLING_STORE_API_ACTIONS.loadCrossSellingSuccess({
                triggerProductId: productId,
                crossSellingRule:
                  data ??
                  ({
                    title: {
                      fr: '',
                    },
                    description: {
                      fr: '',
                    },
                    id: '',
                    name: '',
                    proposals: [],
                  } as CrossSellingProposals),
              });
            },
          },
        );
      }),
    );
  });

  _openCrossSellingModalEffect = createEffect(
    () => {
      return this.actions$.pipe(ofType(PRODUCT_STORE_COMPONENT_ACTIONS.selectProduct)).pipe(
        switchMap(({ productId }) =>
          combineLatest({
            updateProductInCart: this.actions$
              .pipe(ofType(CART_ACTIONS.updateProductInCart))
              .pipe(delay(0)), // avoid a strange case in webapp, when the the add/edit product modal is opened and the cross selling modal is opened at the same time.
            crossSellingData: this.store.select(selectCrossSellingData),
            requestedProductId: of(productId),
          }).pipe(
            filter(
              ({ updateProductInCart, crossSellingData, requestedProductId }) =>
                (updateProductInCart.action === 'add' ||
                  updateProductInCart.action === 'increase') &&
                crossSellingData?.triggerProductId === requestedProductId &&
                updateProductInCart.productId === requestedProductId,
            ),
            take(1),
            filter(
              ({ crossSellingData }) =>
                (crossSellingData?.crossSellingRule.proposals?.length ?? 0) >= 1,
            ),
            tap(({ updateProductInCart, crossSellingData }) => {
              this.modalsService.open('cross-selling-modal', undefined, {
                triggerProductId: updateProductInCart.productId,
                products: crossSellingData?.crossSellingRule.proposals ?? [],
                crossSellingRuleId: crossSellingData?.crossSellingRule.id ?? '',
                title: crossSellingData?.crossSellingRule.title,
                description: crossSellingData?.crossSellingRule.description,
              });
            }),
          ),
        ),
      );
    },
    {
      dispatch: false,
    },
  );
}
